import { AccountMeta, PublicKey, Transaction, TransactionInstruction, TransactionSignature } from '@solana/web3.js';
import { encode } from 'bs58';
import { parseBigInt } from './strings';
import { NeonEmulate, SPLToken, SolanaAccount, authAccountAddress, createApproveDepositInstruction } from '@neonevm/token-transfer-core';
import { decodeRlp, TransactionLike, TransactionRequest, TransactionResponse, Wallet } from 'ethers';
import { Amount, PreparatorySolanaInstruction, SolanaAccountData } from '../models';
import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { HexString } from '../api/models';


export function solanaTransactionLog(transaction: Transaction): string {
  return transaction.instructions.map(({ programId, keys, data }, index) => {
    const result = [];
    result.push(`[${index}] programId: ${programId.toBase58()}`);
    result.push(`keys: ${keys.map(k => `${k.pubkey.toBase58()} [${k.isSigner ? 'signer' : ''}${k.isSigner && k.isWritable ? ', ' : ''}${k.isWritable ? 'writer' : ''}]`).join('\n')}`);
    result.push(`data: ${encode(data)}\n`, `0x${Buffer.from(data).toString('hex')}`);
    return result.join('\n');
  }).join(`\n\n`);
}

export function solanaLog(transaction?: Transaction, signature?: TransactionSignature): string {
  const result: string[] = [];
  if (transaction) {
    result.push(solanaTransactionLog(transaction));
  }
  if (signature) {
    result.push(`Signature: ${signature}`);
  }
  return result.join('\n');
}

export function neonLog(transaction?: TransactionRequest, signature?: TransactionResponse): string {
  const result: string[] = [];
  if (transaction) {
    result.push(`Transaction: ${JSON.stringify(transaction, parseBigInt)}`);
  }
  if (signature) {
    result.push(`Signature: ${signature.blockHash}`);
  }
  return result.join('\n');
}


export function createClaimInstructionSync(neonEmulate: NeonEmulate): {
  neonKeys: AccountMeta[];
  legacyAccounts: SolanaAccount[];
} {
  const legacyAccounts: SolanaAccount[] = [];
  const accountsMap = new Map<string, AccountMeta>();
  if (neonEmulate!) {
    const { accounts = [], solanaAccounts = [] } = neonEmulate;
    for (const account of accounts) {
      const key = account['account'];
      accountsMap.set(key, { pubkey: new PublicKey(key), isSigner: false, isWritable: true });
      if (account['contract']) {
        const key = account['contract'];
        accountsMap.set(key, { pubkey: new PublicKey(key), isSigner: false, isWritable: true });
      }
    }

    for (const account of solanaAccounts) {
      const { pubkey, isLegacy, isWritable } = account;
      accountsMap.set(pubkey, {
        pubkey: new PublicKey(pubkey),
        isSigner: false,
        isWritable
      });
      if (isLegacy) {
        legacyAccounts.push(account);
      }
    }
  }
  return { neonKeys: Array.from(accountsMap.values()), legacyAccounts };
}

export function createApproveInstruction(neonEvmProgram: PublicKey, emulateSigner: Wallet, solanaWallet: PublicKey, splToken: SPLToken, amount: bigint): TransactionInstruction {
  const [delegatePDA] = authAccountAddress(emulateSigner.address, neonEvmProgram, splToken);
  const associatedTokenAddress = getAssociatedTokenAddressSync(new PublicKey(splToken.address_spl), solanaWallet);
  return createApproveDepositInstruction({ solanaWallet, neonPDAWallet: delegatePDA, associatedToken: associatedTokenAddress, amount })
}

export function prepareSolanaInstruction(instruction: TransactionInstruction): PreparatorySolanaInstruction {
  const data = encode(instruction.data);
  const programId = instruction.programId.toBase58();
  const accounts: SolanaAccountData[] = [];
  for (const { pubkey, isSigner, isWritable } of instruction.keys) {
    accounts.push({ address: pubkey.toBase58(), isSigner, isWritable });
  }
  return { programId, data, accounts };
}

export function prepareSolanaInstructions(instructions: TransactionInstruction[]): PreparatorySolanaInstruction[] {
  const result: PreparatorySolanaInstruction[] = [];
  for (const instruction of instructions) {
    result.push(prepareSolanaInstruction(instruction));
  }
  return result;
}

export function decodeTransaction(data: HexString): TransactionLike {
  const decodedData = decodeRlp(data);
  const keys = ['type', 'from', 'to', 'data', 'value', 'gasPrice', 'gasLimit', 'chainId'];
  const transaction: any = {};
  for (let i = 0; i < keys.length; i++) {
    const item = decodedData[i];
    if (!['0x0', '0x'].includes(item as string)) {
      transaction[keys[i]] = decodedData[i];
    }
  }
  return transaction;
}
